import React, { useCallback, type ReactNode } from 'react';
import Link from '@atlaskit/link/link';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import type { ApplicationLinkV3Status } from './CommonApplicationLinkCreationEntity.tsx';
import {
	AUTH_DISABLED,
	AUTH_OAUTH1,
	AUTH_OAUTH1_IMPERSONATION,
	AUTH_UNKNOWN,
	getAuthLevel,
} from './getAuthLevel.tsx';

const authLevelMismatchIncomingMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-mismatch-incoming',
	defaultMessage: 'incoming',
	description: 'Incoming connection text for auth level mismatch',
});

const authLevelMismatchOutgoingMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-mismatch-outgoing',
	defaultMessage: 'outgoing',
	description: 'Outgoing connection text for auth level mismatch',
});

const authTypeImpersonationMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-mismatch-impersonation',
	defaultMessage: 'OAuth with impersonation',
	description:
		'Shown for the auth type "OAuth with impersonation" - please translate impersonation but do not translate "OAuth"',
});

const authTypeDisabledMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-mismatch-disabled',
	defaultMessage: 'Disabled',
	description:
		'The text is shown as a label in the authentication level mismatch detail panel to indicate that authentication is disabled on one side of the application link connection.',
});

const authLevelMismatchUnknownDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-mismatch-unknown-description',
	defaultMessage:
		"We couldn't detect the authentication method for this application link. Make sure both the local and remote connections use the same authentication type.",
	description: 'Description for unknown auth level mismatch',
});

const authLevelMismatchDisabledOauthDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-mismatch-disabled-oauth-description',
	defaultMessage:
		'The local {direction} connection is disabled, however <link>{applicationName}</link> uses {remoteAuthType}.',
	description:
		'Description for disabled-to-oauth auth level mismatch. {direction} is either "incoming" or "outgoing" (localised). {applicationName} is Jira, Confluence, etc. {remoteAuthType} is "OAuth", "Disabled", "OAuth with Impersonation".',
});

const authLevelMismatchOauthOauthDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-mismatch-oauth-oauth-description',
	defaultMessage:
		'The local {direction} connection uses {localAuthType}, however <link>{applicationName}</link> uses {remoteAuthType}.',
	description:
		'Description for oauth-to-oauth auth level mismatch. {direction} is either "incoming" or "outgoing" (localised). {applicationName} is Jira, Confluence, etc. {localAuthType} is "OAuth", "Disabled", "OAuth with Impersonation". {remoteAuthType} is "OAuth", "Disabled", "OAuth with Impersonation".',
});

const authLevelMismatchOauthDisabledDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-mismatch-oauth-disabled-description',
	defaultMessage:
		"The local {direction} connection uses {localAuthType}, however it's disabled on <link>{applicationName}</link>.",
	description:
		'Description for oauth-to-disabled auth level mismatch. {direction} is either "incoming" or "outgoing" (localised). {applicationName} is Jira, Confluence, etc. {localAuthType} is "OAuth", "Disabled", "OAuth with Impersonation".',
});

const messages = {
	authLevelMismatchIncoming: authLevelMismatchIncomingMessage,
	authLevelMismatchOutgoing: authLevelMismatchOutgoingMessage,
	authTypeImpersonation: authTypeImpersonationMessage,
	authTypeDisabled: authTypeDisabledMessage,
	authLevelMismatchUnknownDescription: authLevelMismatchUnknownDescriptionMessage,
	authLevelMismatchDisabledOauthDescription: authLevelMismatchDisabledOauthDescriptionMessage,
	authLevelMismatchOauthOauthDescription: authLevelMismatchOauthOauthDescriptionMessage,
	authLevelMismatchOauthDisabledDescription: authLevelMismatchOauthDisabledDescriptionMessage,
};

export type LocalRemoteMismatchState =
	| 'disabled-oauth'
	| 'disabled-impersonation'
	| 'oauth-disabled'
	| 'oauth-impersonation'
	| 'impersonation-disabled'
	| 'impersonation-oauth'
	| 'unknown'
	| 'no-mismatch';

export type AuthDirection = 'incoming' | 'outgoing';

type PerDirectionDescriptionProps = {
	mismatchState: LocalRemoteMismatchState;
	direction: AuthDirection;
	displayUrl: string;
	name: string;
};

const PerDirectionDescription = (props: PerDirectionDescriptionProps) => {
	const { formatMessage } = useIntl();

	const buildDescriptionForMismatchState = useCallback(
		(direction: AuthDirection) => {
			const directionString =
				direction === 'incoming'
					? formatMessage(messages.authLevelMismatchIncoming)
					: formatMessage(messages.authLevelMismatchOutgoing);

			switch (props.mismatchState) {
				case 'disabled-oauth':
				case 'disabled-impersonation': {
					const remoteAuthTypeString =
						props.mismatchState === 'disabled-oauth'
							? formatMessage(messages.authTypeDisabled)
							: formatMessage(messages.authTypeImpersonation);

					return formatMessage(messages.authLevelMismatchDisabledOauthDescription, {
						direction: directionString,
						applicationName: props.name,
						remoteAuthType: remoteAuthTypeString,
						link: (chunks: ReactNode[]) => (
							<Link href={props.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					});
				}

				case 'oauth-disabled':
				case 'impersonation-disabled': {
					const localAuthTypeString =
						props.mismatchState === 'oauth-disabled'
							? 'OAuth' // not translated, technical term
							: formatMessage(messages.authTypeImpersonation);

					return formatMessage(messages.authLevelMismatchOauthDisabledDescription, {
						direction: directionString,
						applicationName: props.name,
						localAuthType: localAuthTypeString,
						link: (chunks: ReactNode[]) => (
							<Link href={props.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					});
				}

				case 'oauth-impersonation':
				case 'impersonation-oauth': {
					const localAuthTypeString =
						props.mismatchState === 'oauth-impersonation'
							? 'OAuth' // not translated, technical term
							: formatMessage(messages.authTypeImpersonation);
					const remoteAuthTypeString =
						props.mismatchState === 'oauth-impersonation'
							? formatMessage(messages.authTypeImpersonation)
							: 'OAuth'; // not translated, technical term

					return formatMessage(messages.authLevelMismatchOauthOauthDescription, {
						direction: directionString,
						applicationName: props.name,
						localAuthType: localAuthTypeString,
						remoteAuthType: remoteAuthTypeString,
						link: (chunks: ReactNode[]) => (
							<Link href={props.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					});
				}

				default:
					return <></>;
			}
		},
		[formatMessage, props.displayUrl, props.mismatchState, props.name],
	);

	return <Box>{buildDescriptionForMismatchState(props.direction)}</Box>;
};

const determineMismatchState = (
	status: ApplicationLinkV3Status,
	direction: AuthDirection,
): LocalRemoteMismatchState => {
	const local = getAuthLevel(status.localAuthentication?.[direction]);
	const remote = getAuthLevel(status.remoteAuthentication?.[direction]);

	if (local === AUTH_UNKNOWN || remote === AUTH_UNKNOWN) {
		return 'unknown';
	}

	if (local === AUTH_DISABLED && remote === AUTH_OAUTH1) {
		return 'disabled-oauth';
	}
	if (local === AUTH_DISABLED && remote === AUTH_OAUTH1_IMPERSONATION) {
		return 'disabled-impersonation';
	}
	if (local === AUTH_OAUTH1 && remote === AUTH_DISABLED) {
		return 'oauth-disabled';
	}
	if (local === AUTH_OAUTH1_IMPERSONATION && remote === AUTH_DISABLED) {
		return 'impersonation-disabled';
	}
	if (local === AUTH_OAUTH1_IMPERSONATION && remote === AUTH_DISABLED) {
		return 'impersonation-disabled';
	}
	if (local === AUTH_OAUTH1_IMPERSONATION && remote === AUTH_OAUTH1) {
		return 'impersonation-oauth';
	}

	return 'no-mismatch';
};

type InnerDescriptionProps = {
	incomingMismatch: LocalRemoteMismatchState;
	outgoingMismatch: LocalRemoteMismatchState;
	displayUrl: string;
	name: string;
};

export const InnerDescription = (props: InnerDescriptionProps): string | React.JSX.Element => {
	const { formatMessage } = useIntl();

	if (props.incomingMismatch === 'unknown' || props.outgoingMismatch === 'unknown') {
		return formatMessage(messages.authLevelMismatchUnknownDescription);
	}

	return (
		<Stack space="space.100">
			<PerDirectionDescription
				direction="incoming"
				mismatchState={props.incomingMismatch}
				displayUrl={props.displayUrl}
				name={props.name}
			/>
			<PerDirectionDescription
				direction="outgoing"
				mismatchState={props.outgoingMismatch}
				displayUrl={props.displayUrl}
				name={props.name}
			/>
		</Stack>
	);
};

const AuthLevelMismatchDescription = (props: {
	status: ApplicationLinkV3Status;
}): React.JSX.Element => {
	const incomingMismatch = determineMismatchState(props.status, 'incoming');
	const outgoingMismatch = determineMismatchState(props.status, 'outgoing');

	return (
		<InnerDescription
			incomingMismatch={incomingMismatch}
			outgoingMismatch={outgoingMismatch}
			displayUrl={props.status.link.displayUrl}
			name={props.status.link.name}
		/>
	);
};

export default AuthLevelMismatchDescription;
